import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Locale } from '@diana/shared'
import { useContent } from '../../hooks/useContent.js'
import { OfferCard } from '../../components/OfferCard.js'
import { NewsletterForm } from '../../components/NewsletterForm.js'
import { Section, SectionHead } from '../../components/Section.js'
import { useLocale, usePath } from '../../i18n/index.js'

type Specialisation = { title: string; sub: string; body: string }

type HomeStrings = {
  heroGreeting: string
  heroHeading: ReactNode
  heroSub: string
  heroCta: string
  heroCtaSecondary: string
  heroNote: string
  stats: { years: string; students: string; hours: string; reviews: string }
  specEyebrow: string
  specTitle: ReactNode
  specIntro: string
  /** The three things Diana specialises in — mirrors the reference's structure. */
  specialisations: Specialisation[]
  offersEyebrow: string
  offersTitle: ReactNode
  offersIntro: string
  offersAll: string
  testimonialsEyebrow: string
  testimonialsTitle: ReactNode
  quoteMark: string
  faqEyebrow: string
  faqTitle: string
  socialEyebrow: string
  socialTitle: ReactNode
  ctaEyebrow: string
  ctaTitle: ReactNode
  ctaBody: string
  ctaPrimary: string
  ctaSecondary: string
  errorTitle: string
  errorBody: string
}

const T: Record<Locale, HomeStrings> = {
  pl: {
    heroGreeting: 'Cześć, jestem Diana!',
    heroHeading: (
      <>
        Twój przewodnik po <em className="not-italic text-gold">naturalnie brzmiącym</em> polskim
      </>
    ),
    heroSub:
      'Nauczę Cię mówić tak, żeby ludzie słyszeli, co mówisz — a nie skąd jesteś. Bez wkuwania tabelek, bez wstydu, w Twoim tempie.',
    heroCta: 'Umów bezpłatną rozmowę',
    heroCtaSecondary: 'Zobacz, jak uczę',
    heroNote: '30 minut · bez zobowiązań · online',
    stats: {
      years: 'lat doświadczenia',
      students: 'uczniów',
      hours: 'godzin zajęć',
      reviews: 'opinii',
    },
    specEyebrow: 'Specjalizacje',
    specTitle: (
      <>
        W czym <em className="not-italic text-gold">jestem dobra</em>
      </>
    ),
    specIntro: 'Nie uczę wszystkiego wszystkich. Uczę trzech rzeczy, i uczę ich dobrze.',
    specialisations: [
      {
        title: 'Egzaminy językowe',
        sub: 'Jestem do Twojej dyspozycji',
        body: 'Certyfikat B1 do obywatelstwa, C1 na studia, egzamin państwowy. Wiem, jak wyglądają, wiem, co sprawdzają, i wiem, gdzie ludzie tracą punkty przez rzeczy, których nikt im nie powiedział.',
      },
      {
        title: 'Język ogólny',
        sub: 'Zrobimy wszystko to, czego potrzebujesz',
        body: 'Rozmowa w sklepie, u lekarza, na urodzinach teściowej. Polski, którym naprawdę się mówi — z całym tym skracaniem, wtrącaniem i „no dobra".',
      },
      {
        title: 'Język specjalistyczny',
        sub: 'Już znalazłeś odpowiednią osobę',
        body: 'Polski w pracy: spotkania, maile, żargon Twojej branży. Uczę tego, czego naprawdę używasz od poniedziałku do piątku, a nie tego, co jest w rozdziale siódmym.',
      },
    ],
    offersEyebrow: 'Oferta',
    offersTitle: (
      <>
        Jak możemy <em className="not-italic text-gold">pracować razem</em>
      </>
    ),
    offersIntro:
      'Przejrzyste ceny, bez ukrytych kosztów. Nie wiesz, co wybrać? Zacznij od bezpłatnej rozmowy.',
    offersAll: 'Zobacz pełen cennik',
    testimonialsEyebrow: 'Opinie',
    testimonialsTitle: (
      <>
        Co mówią <em className="not-italic text-gold">moi uczniowie</em>
      </>
    ),
    quoteMark: '„',
    faqEyebrow: 'Pytania',
    faqTitle: 'Zanim zaczniemy',
    socialEyebrow: 'Odwiedź mnie',
    socialTitle: (
      <>
        Codziennie uczę <em className="not-italic text-gold">po kawałku</em>
      </>
    ),
    ctaEyebrow: 'Zaczynajmy',
    ctaTitle: (
      <>
        Odezwij się do mnie, jeśli chcesz zacząć swoją{' '}
        <em className="not-italic text-gold-300">najlepszą przygodę</em> z językiem polskim
      </>
    ),
    ctaBody:
      'Pierwsza rozmowa jest bezpłatna i do niczego nie zobowiązuje. Porozmawiamy o tym, gdzie jesteś i dokąd chcesz dojść.',
    ctaPrimary: 'Umów bezpłatną rozmowę',
    ctaSecondary: 'Napisz do mnie',
    errorTitle: 'Coś poszło nie tak',
    errorBody: 'Odśwież stronę za chwilę.',
  },

  uk: {
    heroGreeting: 'Привіт, я Діана!',
    heroHeading: (
      <>
        Твій провідник у світ польської, що звучить{' '}
        <em className="not-italic text-gold">природно</em>
      </>
    ),
    heroSub:
      'Навчу тебе говорити так, щоб люди чули, що ти кажеш, — а не звідки ти. Без зубріння таблиць, без сорому, у твоєму темпі.',
    heroCta: 'Записатися на безкоштовну розмову',
    heroCtaSecondary: 'Подивись, як я навчаю',
    heroNote: '30 хвилин · без зобов’язань · онлайн',
    stats: {
      years: 'років досвіду',
      students: 'учнів',
      hours: 'годин занять',
      reviews: 'відгуків',
    },
    specEyebrow: 'Спеціалізації',
    specTitle: (
      <>
        У чому я <em className="not-italic text-gold">справді сильна</em>
      </>
    ),
    specIntro: 'Я не навчаю всього і всіх. Навчаю трьох речей — і навчаю їх добре.',
    specialisations: [
      {
        title: 'Мовні іспити',
        sub: 'Я у твоєму розпорядженні',
        body: 'Сертифікат B1 для громадянства, C1 для навчання, державний іспит. Я знаю, який вони мають вигляд, знаю, що перевіряють, і знаю, де люди втрачають бали через речі, про які їм ніхто не сказав.',
      },
      {
        title: 'Повсякденна мова',
        sub: 'Зробимо все, що тобі потрібно',
        body: 'Розмова в магазині, у лікаря, на дні народження свекрухи. Польська, якою справді говорять — з усіма цими скороченнями, вставками і «no dobra».',
      },
      {
        title: 'Професійна мова',
        sub: 'Ти вже знайшов потрібну людину',
        body: 'Польська на роботі: зустрічі, мейли, жаргон твоєї галузі. Навчаю того, чим ти справді користуєшся з понеділка по п’ятницю, а не того, що написано в сьомому розділі.',
      },
    ],
    offersEyebrow: 'Пропозиція',
    offersTitle: (
      <>
        Як ми можемо <em className="not-italic text-gold">працювати разом</em>
      </>
    ),
    offersIntro:
      'Прозорі ціни, без прихованих витрат. Не знаєш, що обрати? Почни з безкоштовної розмови.',
    offersAll: 'Переглянути повний прайс',
    testimonialsEyebrow: 'Відгуки',
    testimonialsTitle: (
      <>
        Що кажуть <em className="not-italic text-gold">мої учні</em>
      </>
    ),
    quoteMark: '«',
    faqEyebrow: 'Питання',
    faqTitle: 'Перш ніж почнемо',
    socialEyebrow: 'Завітай до мене',
    socialTitle: (
      <>
        Щодня навчаю <em className="not-italic text-gold">потроху</em>
      </>
    ),
    ctaEyebrow: 'Починаймо',
    ctaTitle: (
      <>
        Напиши мені, якщо хочеш почати свою{' '}
        <em className="not-italic text-gold-300">найкращу пригоду</em> з польською мовою
      </>
    ),
    ctaBody:
      'Перша розмова безкоштовна й ні до чого не зобов’язує. Поговоримо про те, де ти зараз і куди хочеш дійти.',
    ctaPrimary: 'Записатися на безкоштовну розмову',
    ctaSecondary: 'Напиши мені',
    errorTitle: 'Щось пішло не так',
    errorBody: 'Онови сторінку за хвилинку.',
  },

  ru: {
    heroGreeting: 'Привет, я Диана!',
    heroHeading: (
      <>
        Твой проводник в мир польского, который звучит{' '}
        <em className="not-italic text-gold">естественно</em>
      </>
    ),
    heroSub:
      'Научу тебя говорить так, чтобы люди слышали, что ты говоришь, — а не откуда ты. Без зубрёжки таблиц, без стеснения, в твоём темпе.',
    heroCta: 'Записаться на бесплатную встречу',
    heroCtaSecondary: 'Посмотри, как я преподаю',
    heroNote: '30 минут · без обязательств · онлайн',
    stats: {
      years: 'лет опыта',
      students: 'учеников',
      hours: 'часов занятий',
      reviews: 'отзывов',
    },
    specEyebrow: 'Специализации',
    specTitle: (
      <>
        В чём я <em className="not-italic text-gold">действительно сильна</em>
      </>
    ),
    specIntro: 'Я не учу всему и всех. Учу трём вещам — и учу им хорошо.',
    specialisations: [
      {
        title: 'Языковые экзамены',
        sub: 'Я в твоём распоряжении',
        body: 'Сертификат B1 для гражданства, C1 для учёбы, государственный экзамен. Я знаю, как они выглядят, знаю, что проверяют, и знаю, где люди теряют баллы из-за вещей, о которых им никто не сказал.',
      },
      {
        title: 'Повседневный язык',
        sub: 'Сделаем всё, что тебе нужно',
        body: 'Разговор в магазине, у врача, на дне рождения тёщи. Польский, на котором действительно говорят — со всеми этими сокращениями, вставками и «no dobra».',
      },
      {
        title: 'Профессиональный язык',
        sub: 'Ты уже нашёл нужного человека',
        body: 'Польский на работе: встречи, письма, жаргон твоей отрасли. Учу тому, чем ты действительно пользуешься с понедельника по пятницу, а не тому, что написано в седьмой главе.',
      },
    ],
    offersEyebrow: 'Предложение',
    offersTitle: (
      <>
        Как мы можем <em className="not-italic text-gold">работать вместе</em>
      </>
    ),
    offersIntro:
      'Прозрачные цены, без скрытых расходов. Не знаешь, что выбрать? Начни с бесплатной встречи.',
    offersAll: 'Посмотреть полный прайс',
    testimonialsEyebrow: 'Отзывы',
    testimonialsTitle: (
      <>
        Что говорят <em className="not-italic text-gold">мои ученики</em>
      </>
    ),
    quoteMark: '«',
    faqEyebrow: 'Вопросы',
    faqTitle: 'Прежде чем начнём',
    socialEyebrow: 'Заходи ко мне',
    socialTitle: (
      <>
        Каждый день учу <em className="not-italic text-gold">по чуть-чуть</em>
      </>
    ),
    ctaEyebrow: 'Начинаем',
    ctaTitle: (
      <>
        Напиши мне, если хочешь начать своё{' '}
        <em className="not-italic text-gold-300">лучшее приключение</em> с польским языком
      </>
    ),
    ctaBody:
      'Первая встреча бесплатная и ни к чему не обязывает. Поговорим о том, где ты сейчас и куда хочешь прийти.',
    ctaPrimary: 'Записаться на бесплатную встречу',
    ctaSecondary: 'Напиши мне',
    errorTitle: 'Что-то пошло не так',
    errorBody: 'Обнови страницу через минутку.',
  },
}

export function HomePage() {
  const locale = useLocale()
  const to = usePath()
  const s = T[locale]
  const { data, isLoading, isError } = useContent(locale)

  if (isLoading) return <PageSkeleton />
  if (isError || !data) return <LoadError />

  const copy = data.pages.home?.content ?? {}
  const stats = (data.settings.stats ?? {}) as Record<string, number | null>
  const social = (data.settings.social ?? {}) as Record<string, string>
  const hero = (data.settings.hero ?? {}) as { url?: string; alt?: string }
  const hasStats = Object.values(stats).some((v) => v != null)
  const socialLinks = Object.entries(social).filter(([, v]) => v)

  return (
    <>
      {/* ── hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-sky-100/60 blur-3xl" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:pb-28 lg:pt-24">
          <div>
            <p className="h-display text-xl italic text-gold-600 sm:text-2xl">
              {s.heroGreeting}
            </p>

            <h1 className="h-display mt-5 text-[2.6rem] leading-[1.08] sm:text-6xl">
              {copy.heroHeading ?? s.heroHeading}
            </h1>

            <div className="rule-gold mt-8 w-28" />

            <p className="lead mt-8 max-w-xl">
              {copy.heroSub ?? s.heroSub}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={to('/rezerwacja')} className="btn btn-gold">
                {copy.heroCta ?? s.heroCta}
              </Link>
              <Link to={to('/ucz-sie-ze-mna')} className="btn btn-outline">
                {copy.heroCtaSecondary ?? s.heroCtaSecondary}
              </Link>
            </div>

            <p className="mt-6 font-roboto text-xs uppercase tracking-[0.18em] text-ink-faint">
              {s.heroNote}
            </p>
          </div>

          {/*
            Diana's photograph — chosen from the panel (Start → Zdjęcie na
            stronie głównej). Until she picks one, the gold frame holds a
            quiet gradient rather than a stock photo.
          */}
          <div
            className={[
              'relative mx-auto w-full max-w-sm',
              hero.url ? 'block' : 'hidden lg:block',
            ].join(' ')}
          >
            <div className="absolute -inset-3 rounded-sm border border-gold/40" />
            {hero.url ? (
              <img
                src={hero.url}
                alt={hero.alt || 'Diana'}
                width={400}
                height={500}
                className="relative aspect-[4/5] w-full rounded-sm object-cover"
              />
            ) : (
              <div className="relative aspect-[4/5] bg-gradient-to-br from-sky-100 to-sky-200" />
            )}
          </div>
        </div>
      </section>

      {/* ── trust bar ─────────────────────────────────────────────────── */}
      {hasStats && (
        <section className="border-y border-gold/20 bg-ivory-deep">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-8 px-5 py-12 text-center sm:grid-cols-4 lg:px-8">
            <Stat value={stats.years} label={s.stats.years} />
            <Stat value={stats.students} label={s.stats.students} />
            <Stat value={stats.hours} label={s.stats.hours} />
            <Stat value={stats.reviews} label={s.stats.reviews} />
          </div>
        </section>
      )}

      {/* ── specialisations ───────────────────────────────────────────── */}
      <Section>
        <SectionHead
          eyebrow={s.specEyebrow}
          title={s.specTitle}
          intro={s.specIntro}
        />

        <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-sky-200 bg-sky-200 md:grid-cols-3">
          {s.specialisations.map((spec) => (
            <article key={spec.title} className="bg-porcelain p-8 lg:p-10">
              <h3 className="h-condensed text-lg text-ink">{spec.title}</h3>
              <p className="h-display mt-2 text-base italic text-gold-600">{spec.sub}</p>
              <div className="rule-gold mt-5 w-12" />
              <p className="mt-5 text-[0.95rem] leading-relaxed">{spec.body}</p>
            </article>
          ))}
        </div>
      </Section>

      {/* ── offers ────────────────────────────────────────────────────── */}
      {data.offers.length > 0 && (
        <section className="bg-ivory-deep py-24 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <SectionHead
              eyebrow={s.offersEyebrow}
              title={s.offersTitle}
              intro={s.offersIntro}
            />

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {data.offers.map((offer) => (
                <OfferCard key={offer.id} offer={offer} />
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link to={to('/ucz-sie-ze-mna')} className="btn btn-outline">
                {s.offersAll}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── testimonials ──────────────────────────────────────────────── */}
      {data.testimonials.length > 0 && (
        <Section>
          <SectionHead
            eyebrow={s.testimonialsEyebrow}
            title={s.testimonialsTitle}
          />

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {data.testimonials.slice(0, 4).map((t) => (
              <figure key={t.id} className="card p-8 lg:p-10">
                <span aria-hidden className="h-display block text-5xl leading-none text-gold-300">
                  {s.quoteMark}
                </span>
                <blockquote className="mt-3 text-[1.05rem] leading-relaxed font-light">{t.text}</blockquote>
                <div className="rule-gold mt-6 w-12" />
                <figcaption className="mt-4 font-roboto text-xs uppercase tracking-[0.16em] text-ink-faint">
                  <span className="font-bold text-ink">{t.studentName}</span>
                  {t.country && ` · ${t.country}`}
                  {t.level && ` · ${t.level}`}
                </figcaption>
              </figure>
            ))}
          </div>
        </Section>
      )}

      {/* ── faq ───────────────────────────────────────────────────────── */}
      {data.faqs.length > 0 && (
        <section className="bg-ivory-deep py-24 lg:py-28">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <SectionHead eyebrow={s.faqEyebrow} title={s.faqTitle} />
            <dl className="mt-12 divide-y divide-sky-200">
              {data.faqs.map((faq) => (
                <div key={faq.id} className="py-7">
                  <dt className="h-condensed text-base text-ink">{faq.question}</dt>
                  <dd className="mt-3 leading-relaxed font-light text-ink-soft">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* ── visit me ──────────────────────────────────────────────────── */}
      {socialLinks.length > 0 && (
        <Section>
          <div className="text-center">
            <p className="eyebrow justify-center">{s.socialEyebrow}</p>
            <h2 className="h-display mt-5 text-3xl sm:text-4xl">
              {s.socialTitle}
            </h2>
            <div className="rule-gold-center mx-auto mt-8 w-40" />
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {socialLinks.map(([key, url]) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-outline btn-sm"
                >
                  {key}
                </a>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ── closing CTA ───────────────────────────────────────────────── */}
      <section className="bg-ink py-24 text-center lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="eyebrow justify-center !text-gold-300">{s.ctaEyebrow}</p>
          <h2 className="h-display mt-6 text-3xl leading-tight text-ivory sm:text-[2.75rem]">
            {s.ctaTitle}
          </h2>
          <p className="mx-auto mt-7 max-w-xl text-[1.05rem] leading-relaxed font-light text-sky-200/80">
            {s.ctaBody}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to={to('/rezerwacja')}
              className="btn border border-gold-300 bg-gold-300 text-ink hover:bg-transparent hover:text-gold-300"
            >
              {s.ctaPrimary}
            </Link>
            <Link
              to={to('/kontakt')}
              className="btn border border-sky-100/30 text-sky-100 hover:border-gold-300 hover:text-gold-300"
            >
              {s.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      <Section>
        <NewsletterForm />
      </Section>
    </>
  )
}

function Stat({ value, label }: { value: number | null | undefined; label: string }) {
  if (value == null) return null
  return (
    <div>
      <p className="h-display text-4xl text-gold sm:text-5xl">{value}</p>
      <p className="mt-2 font-roboto text-[0.7rem] uppercase tracking-[0.18em] text-ink-faint">{label}</p>
    </div>
  )
}

function PageSkeleton() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse px-5 pt-24 lg:px-8">
      <div className="h-6 w-48 bg-sky-100" />
      <div className="mt-6 h-16 w-4/5 bg-sky-100" />
      <div className="mt-4 h-16 w-3/5 bg-sky-100" />
      <div className="mt-14 grid gap-6 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-64 bg-sky-100" />
        ))}
      </div>
    </div>
  )
}

function LoadError() {
  const locale = useLocale()
  const s = T[locale]
  return (
    <div className="mx-auto max-w-3xl px-5 py-32 text-center">
      <h1 className="h-display text-3xl italic">{s.errorTitle}</h1>
      <p className="mt-4 font-light text-ink-soft">{s.errorBody}</p>
    </div>
  )
}
